import { NgClass } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { EventModel } from '@app/events/shared/event.model';
import { EventPlannerSupplierAttachmentModel, EventPlannerSupplierModel } from '@app/events/shared/event-planner-supplier.model';
import { EventPlannerSupplierService } from '@app/events/shared/event-planner-supplier.service';
import { ButtonComponent } from '@app/partials/form/button/button.component';
import { SelectComponent } from '@app/partials/form/select/select.component';
import { TextInputComponent } from '@app/partials/form/text-input/text-input.component';
import { TextareaComponent } from '@app/partials/form/textarea/textarea.component';
import { TimePickerComponent } from '@app/partials/form/time-picker/time-picker.component';
import { ConfirmationModalService } from '@app/partials/modal/confirmation-modal.service';
import { SignedUploadService, UploadedFileReference } from '@app/shared/uploads/signed-upload.service';
import { ToastService } from '@app/shared/toast/toast.service';
import { FileUp, LucideAngularModule, Plus, Trash2 } from 'lucide-angular';

type SupplierAttachmentKind = 'pli' | 'pat';
type ScrollSnapshot = {
  scrollContainer: HTMLElement | null;
  containerScrollTop: number;
  windowScrollTop: number;
};

@Component({
  selector: 'app-event-planner-suppliers',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    NgClass,
    LucideAngularModule,
    ButtonComponent,
    SelectComponent,
    TextInputComponent,
    TextareaComponent,
    TimePickerComponent,
  ],
  templateUrl: './planner-suppliers.component.html',
  styleUrls: [],
})
export class PlannerSuppliersComponent implements OnInit, OnChanges {
  @Input({ required: true }) public event!: EventModel;
  @Output() public dirtyStateChange = new EventEmitter<boolean>();

  protected readonly PlusIcon = Plus;
  protected readonly FileUpIcon = FileUp;
  protected readonly TrashIcon = Trash2;
  protected readonly statusChoices = [
    { value: 'Yes', label: 'Yes' },
    { value: 'No', label: 'No' },
    { value: 'Pending', label: 'Pending' },
  ];

  protected loading = false;
  protected saving = false;
  protected loadError: string | null = null;
  protected supplierNotesControl = new FormControl('', [Validators.maxLength(10000)]);
  protected suppliersForm = new FormGroup({
    suppliers: new FormArray<FormGroup>([]),
  });

  private removedSupplierIds = new Set<number>();
  private loadedSuppliers: EventPlannerSupplierModel[] = [];
  private loadedSupplierNotes = '';

  constructor(
    private hostElement: ElementRef<HTMLElement>,
    private supplierService: EventPlannerSupplierService,
    private confirmationModal: ConfirmationModalService,
    private signedUploadService: SignedUploadService,
    private toastService: ToastService,
  ) {}

  ngOnInit(): void {
    this.suppliersForm.valueChanges.subscribe(() => this.emitDirtyState());
    this.supplierNotesControl.valueChanges.subscribe(() => this.emitDirtyState());
    this.loadSuppliers();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['event'] && !changes['event'].firstChange && changes['event'].currentValue?.id !== changes['event'].previousValue?.id) {
      this.loadSuppliers();
    }
  }

  protected get supplierGroups(): FormArray<FormGroup> {
    return this.suppliersForm.controls.suppliers;
  }

  protected hasUnsavedChanges(): boolean {
    if (this.isDefaultEmptyDraftState()) {
      return false;
    }

    if (this.removedSupplierIds.size > 0) {
      return true;
    }

    if (this.normalizeValue(this.supplierNotesControl.value) !== this.normalizeValue(this.loadedSupplierNotes)) {
      return true;
    }

    if (this.supplierGroups.length !== this.loadedSuppliers.length) {
      return true;
    }

    return this.supplierGroups.controls.some((_, index) => {
      const supplierId = this.supplierIdControl(index).value;

      if (!supplierId) {
        return true;
      }

      return this.hasSupplierDifferences(index);
    });
  }

  protected supplierAnchorId(index: number): string {
    return `planner-supplier-${index + 1}`;
  }

  protected supplierNotesAnchorId(): string {
    return 'planner-supplier-notes';
  }

  protected hasSuppliers(): boolean {
    return this.supplierGroups.length > 0;
  }

  protected addSupplier(): void {
    const newIndex = this.supplierGroups.length;

    this.supplierGroups.push(this.createSupplierGroup());
    this.suppliersForm.markAsDirty();
    this.emitDirtyState();

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.scrollToAnchor(this.supplierAnchorId(newIndex));
      });
    });
  }

  protected async removeSupplier(index: number): Promise<void> {
    const confirmed = await this.confirmationModal.confirm({
      title: 'Remove Supplier',
      message: 'Remove this supplier from the event planner? This change will be saved when you click Save Changes.',
      confirmLabel: 'Remove Supplier',
      confirmButtonStyle: 'danger',
    });

    if (!confirmed) {
      return;
    }

    const supplierId = this.supplierIdControl(index).value;

    if (supplierId) {
      this.removedSupplierIds.add(supplierId);
    }

    this.supplierGroups.removeAt(index);
    this.suppliersForm.markAsDirty();
    this.emitDirtyState();
  }

  protected async saveChanges(): Promise<void> {
    if (!this.event?.id || this.saving) {
      return;
    }

    if (!this.hasUnsavedChanges()) {
      return;
    }

    this.suppliersForm.markAllAsTouched();
    this.supplierNotesControl.markAsTouched();

    if (this.suppliersForm.invalid || this.supplierNotesControl.invalid) {
      this.toastService.error('Please fix the highlighted supplier fields before saving.');
      return;
    }

    this.saving = true;
    const scrollSnapshot = this.captureScrollPosition();

    try {
      for (const supplierId of this.removedSupplierIds) {
        await firstValueFrom(this.supplierService.deletePlannerSupplier(this.event.id, supplierId));
      }

      for (let index = 0; index < this.supplierGroups.length; index++) {
        const supplierId = this.supplierIdControl(index).value;
        const payload = await this.serializeSupplier(index);

        if (supplierId) {
          await firstValueFrom(this.supplierService.updatePlannerSupplier(this.event.id, supplierId, payload));
        } else {
          await firstValueFrom(this.supplierService.createPlannerSupplier(this.event.id, payload));
        }
      }

      const notesResponse = await firstValueFrom(
        this.supplierService.updateSupplierNotes(this.event.id, this.supplierNotesControl.value?.trim() || null),
      );

      this.event = {
        ...this.event,
        supplier_notes: notesResponse.data.supplier_notes,
      };

      await this.loadSuppliers({ silent: true, restoreScroll: scrollSnapshot, throwOnError: true });
      this.toastService.success('Suppliers updated successfully');
    } catch (error: any) {
      console.error('Error saving planner suppliers:', error);
      this.toastService.error(this.resolveErrorMessage(error));
    } finally {
      this.saving = false;
    }
  }

  protected cancelChanges(): void {
    this.setLoadedState(this.loadedSuppliers, this.loadedSupplierNotes);
  }

  protected onAttachmentSelected(event: Event, supplierIndex: number, kind: SupplierAttachmentKind): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;

    this.fileControl(supplierIndex, kind).setValue(file);
    this.removeAttachmentFlagControl(supplierIndex, kind).setValue(false);

    if (file) {
      this.existingAttachmentControl(supplierIndex, kind).setValue(null);
    }

    this.suppliersForm.markAsDirty();
    this.emitDirtyState();
    input.value = '';
  }

  protected clearAttachment(supplierIndex: number, kind: SupplierAttachmentKind): void {
    const existingAttachment = this.existingAttachmentControl(supplierIndex, kind).value;

    this.fileControl(supplierIndex, kind).setValue(null);

    if (existingAttachment) {
      this.existingAttachmentControl(supplierIndex, kind).setValue(null);
      this.removeAttachmentFlagControl(supplierIndex, kind).setValue(true);
    }

    this.suppliersForm.markAsDirty();
    this.emitDirtyState();
  }

  protected attachmentName(supplierIndex: number, kind: SupplierAttachmentKind): string {
    const file = this.fileControl(supplierIndex, kind).value;
    if (file instanceof File) {
      return file.name;
    }

    return this.existingAttachmentControl(supplierIndex, kind).value?.original_name ?? 'No file chosen';
  }

  protected hasAttachment(supplierIndex: number, kind: SupplierAttachmentKind): boolean {
    return this.fileControl(supplierIndex, kind).value instanceof File || !!this.existingAttachmentControl(supplierIndex, kind).value;
  }

  protected attachmentUrl(supplierIndex: number, kind: SupplierAttachmentKind): string | null {
    return this.existingAttachmentControl(supplierIndex, kind).value?.url ?? null;
  }

  protected supplierCardTitle(index: number): string {
    const supplierType = this.normalizeValue(this.supplierTypeControl(index).value);
    return supplierType || `Supplier ${index + 1}`;
  }

  protected scrollToAnchor(anchorId: string): void {
    const element = document.getElementById(anchorId);

    if (!element) {
      return;
    }

    const scrollContainer = this.findScrollContainer(element);

    if (scrollContainer) {
      const containerRect = scrollContainer.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();
      const nextTop = scrollContainer.scrollTop + (elementRect.top - containerRect.top) - 24;

      scrollContainer.scrollTo({
        top: Math.max(0, nextTop),
        behavior: 'smooth',
      });

      return;
    }

    const nextTop = window.scrollY + element.getBoundingClientRect().top - 24;
    window.scrollTo({
      top: Math.max(0, nextTop),
      behavior: 'smooth',
    });
  }

  protected supplierTypeControl(index: number): FormControl {
    return this.supplierGroups.at(index).get('supplier_type') as FormControl;
  }

  protected companyNameControl(index: number): FormControl {
    return this.supplierGroups.at(index).get('company_name') as FormControl;
  }

  protected contactNameControl(index: number): FormControl {
    return this.supplierGroups.at(index).get('contact_name') as FormControl;
  }

  protected telephoneControl(index: number): FormControl {
    return this.supplierGroups.at(index).get('telephone') as FormControl;
  }

  protected emailAddressControl(index: number): FormControl {
    return this.supplierGroups.at(index).get('email_address') as FormControl;
  }

  protected arrivalTimeControl(index: number): FormControl {
    return this.supplierGroups.at(index).get('arrival_time') as FormControl;
  }

  protected pliStatusControl(index: number): FormControl {
    return this.supplierGroups.at(index).get('pli_status') as FormControl;
  }

  protected patStatusControl(index: number): FormControl {
    return this.supplierGroups.at(index).get('pat_status') as FormControl;
  }

  protected notesControl(index: number): FormControl {
    return this.supplierGroups.at(index).get('notes') as FormControl;
  }

  protected fieldInvalid(control: AbstractControl | null): boolean {
    return !!control && control.invalid && (control.dirty || control.touched);
  }

  private supplierIdControl(index: number): FormControl {
    return this.supplierGroups.at(index).get('id') as FormControl;
  }

  private fileControl(index: number, kind: SupplierAttachmentKind): FormControl {
    return this.supplierGroups.at(index).get(`${kind}_attachment`) as FormControl;
  }

  private existingAttachmentControl(index: number, kind: SupplierAttachmentKind): FormControl<EventPlannerSupplierAttachmentModel | null> {
    return this.supplierGroups.at(index).get(`${kind}_existing_attachment`) as FormControl<EventPlannerSupplierAttachmentModel | null>;
  }

  private removeAttachmentFlagControl(index: number, kind: SupplierAttachmentKind): FormControl<boolean> {
    return this.supplierGroups.at(index).get(`remove_${kind}_attachment`) as FormControl<boolean>;
  }

  protected async loadSuppliers(options: { silent?: boolean; restoreScroll?: ScrollSnapshot | null; throwOnError?: boolean } = {}): Promise<void> {
    if (!this.event?.id) {
      return;
    }

    const { silent = false, restoreScroll = null, throwOnError = false } = options;

    if (!silent) {
      this.loading = true;
      this.loadError = null;
    }

    try {
      const response = await firstValueFrom(this.supplierService.getPlannerSuppliers(this.event.id));
      this.setLoadedState(response.data, this.event.supplier_notes ?? '');

      if (restoreScroll) {
        this.restoreScrollPosition(restoreScroll);
      }
    } catch (error) {
      console.error('Error loading planner suppliers:', error);

      if (!silent) {
        this.loadError = 'Failed to load suppliers for this event.';
      }

      if (throwOnError) {
        throw error;
      }
    } finally {
      if (!silent) {
        this.loading = false;
      }
    }
  }

  private setLoadedState(suppliers: EventPlannerSupplierModel[], supplierNotes: string): void {
    this.loadedSuppliers = JSON.parse(JSON.stringify(suppliers)) as EventPlannerSupplierModel[];
    this.loadedSupplierNotes = supplierNotes ?? '';
    this.removedSupplierIds.clear();
    this.suppliersForm.setControl(
      'suppliers',
      new FormArray<FormGroup>(suppliers.map((supplier) => this.createSupplierGroup(supplier))),
    );
    this.suppliersForm.markAsPristine();
    this.supplierNotesControl.setValue(this.loadedSupplierNotes, { emitEvent: false });
    this.supplierNotesControl.markAsPristine();
    this.supplierNotesControl.markAsUntouched();
    this.emitDirtyState();
  }

  private createSupplierGroup(supplier?: EventPlannerSupplierModel): FormGroup {
    return new FormGroup({
      id: new FormControl<number | null>(supplier?.id ?? null),
      supplier_type: new FormControl(supplier?.supplier_type ?? '', [Validators.required, Validators.maxLength(255)]),
      company_name: new FormControl(supplier?.company_name ?? '', [Validators.maxLength(255)]),
      contact_name: new FormControl(supplier?.contact_name ?? '', [Validators.maxLength(255)]),
      telephone: new FormControl(supplier?.telephone ?? '', [Validators.maxLength(255)]),
      email_address: new FormControl(supplier?.email_address ?? '', [Validators.email, Validators.maxLength(255)]),
      arrival_time: new FormControl(this.normalizeTimeValue(supplier?.arrival_time ?? null)),
      pli_status: new FormControl(supplier?.pli_status ?? null),
      pat_status: new FormControl(supplier?.pat_status ?? null),
      notes: new FormControl(supplier?.notes ?? '', [Validators.maxLength(10000)]),
      pli_attachment: new FormControl<File | null>(null),
      pat_attachment: new FormControl<File | null>(null),
      pli_existing_attachment: new FormControl<EventPlannerSupplierAttachmentModel | null>(supplier?.pli_attachment ?? null),
      pat_existing_attachment: new FormControl<EventPlannerSupplierAttachmentModel | null>(supplier?.pat_attachment ?? null),
      remove_pli_attachment: new FormControl(false, { nonNullable: true }),
      remove_pat_attachment: new FormControl(false, { nonNullable: true }),
    });
  }

  private async serializeSupplier(index: number): Promise<FormData> {
    const group = this.supplierGroups.at(index);
    const formData = new FormData();
    const raw = group.getRawValue();

    formData.append('sort_order', index.toString());
    formData.append('supplier_type', raw.supplier_type.trim());
    this.appendNullableField(formData, 'company_name', raw.company_name);

    this.appendNullableField(formData, 'contact_name', raw.contact_name);
    this.appendNullableField(formData, 'telephone', raw.telephone);
    this.appendNullableField(formData, 'email_address', raw.email_address);
    this.appendNullableField(formData, 'arrival_time', raw.arrival_time);
    this.appendNullableField(formData, 'pli_status', raw.pli_status);
    this.appendNullableField(formData, 'pat_status', raw.pat_status);
    this.appendNullableField(formData, 'notes', raw.notes);

    if (raw.pli_attachment instanceof File) {
      const upload = await this.uploadAttachment(raw.pli_attachment);
      formData.append('pli_attachment_key', upload.key);
      formData.append('pli_attachment_original_name', upload.originalName);
    }

    if (raw.pat_attachment instanceof File) {
      const upload = await this.uploadAttachment(raw.pat_attachment);
      formData.append('pat_attachment_key', upload.key);
      formData.append('pat_attachment_original_name', upload.originalName);
    }

    if (raw.remove_pli_attachment) {
      formData.append('remove_pli_attachment', '1');
    }

    if (raw.remove_pat_attachment) {
      formData.append('remove_pat_attachment', '1');
    }

    return formData;
  }

  private uploadAttachment(file: File): Promise<UploadedFileReference> {
    return this.signedUploadService.upload(file);
  }

  private appendNullableField(formData: FormData, key: string, value: string | null | undefined): void {
    const normalized = value?.trim() ?? '';

    if (normalized) {
      formData.append(key, normalized);
    }
  }

  private normalizeTimeValue(value: string | null): string {
    if (!value) {
      return '';
    }

    return value.length === 5 ? `${value}:00` : value;
  }

  private hasSupplierDifferences(index: number): boolean {
    const supplierId = this.supplierIdControl(index).value;
    const loadedSupplier = this.loadedSuppliers.find((supplier) => supplier.id === supplierId);

    if (!loadedSupplier) {
      return true;
    }

    const raw = this.supplierGroups.at(index).getRawValue();

    return this.normalizeValue(raw.supplier_type) !== this.normalizeValue(loadedSupplier.supplier_type)
      || this.normalizeValue(raw.company_name) !== this.normalizeValue(loadedSupplier.company_name)
      || this.normalizeValue(raw.contact_name) !== this.normalizeValue(loadedSupplier.contact_name)
      || this.normalizeValue(raw.telephone) !== this.normalizeValue(loadedSupplier.telephone)
      || this.normalizeValue(raw.email_address) !== this.normalizeValue(loadedSupplier.email_address)
      || this.normalizeTimeValue(raw.arrival_time ?? null) !== this.normalizeTimeValue(loadedSupplier.arrival_time)
      || (raw.pli_status ?? null) !== loadedSupplier.pli_status
      || (raw.pat_status ?? null) !== loadedSupplier.pat_status
      || this.normalizeValue(raw.notes) !== this.normalizeValue(loadedSupplier.notes)
      || raw.remove_pli_attachment
      || raw.remove_pat_attachment
      || raw.pli_attachment instanceof File
      || raw.pat_attachment instanceof File;
  }

  private isDefaultEmptyDraftState(): boolean {
    return this.loadedSuppliers.length === 0
      && this.removedSupplierIds.size === 0
      && this.supplierGroups.length === 0
      && this.normalizeValue(this.supplierNotesControl.value) === this.normalizeValue(this.loadedSupplierNotes);
  }

  private normalizeValue(value: string | null | undefined): string {
    return (value ?? '').trim();
  }

  private emitDirtyState(): void {
    this.dirtyStateChange.emit(this.hasUnsavedChanges());
  }

  private resolveErrorMessage(error: any): string {
    const errors = error?.error?.errors;

    if (errors && typeof errors === 'object') {
      const firstKey = Object.keys(errors)[0];
      const firstMessage = Array.isArray(errors[firstKey]) ? errors[firstKey][0] : errors[firstKey];

      if (typeof firstMessage === 'string' && firstMessage.length > 0) {
        return firstMessage;
      }
    }

    if (typeof error?.error?.message === 'string' && error.error.message.length > 0) {
      return error.error.message;
    }

    if (typeof error?.message === 'string' && error.message.length > 0) {
      return error.message;
    }

    return 'Failed to save suppliers';
  }

  private findScrollContainer(element: HTMLElement): HTMLElement | null {
    let current: HTMLElement | null = element.parentElement;

    while (current) {
      const styles = window.getComputedStyle(current);
      const overflowY = styles.overflowY;

      if ((overflowY === 'auto' || overflowY === 'scroll') && current.scrollHeight > current.clientHeight) {
        return current;
      }

      current = current.parentElement;
    }

    return null;
  }

  private captureScrollPosition(): ScrollSnapshot {
    const scrollContainer = this.findScrollContainer(this.hostElement.nativeElement);

    return {
      scrollContainer,
      containerScrollTop: scrollContainer?.scrollTop ?? 0,
      windowScrollTop: window.scrollY,
    };
  }

  private restoreScrollPosition(snapshot: ScrollSnapshot): void {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (snapshot.scrollContainer) {
          snapshot.scrollContainer.scrollTo({
            top: snapshot.containerScrollTop,
          });
          return;
        }

        window.scrollTo({
          top: snapshot.windowScrollTop,
        });
      });
    });
  }
}
